import React, { useState } from 'react'
import { Button, Flex, Form, Input, Typography } from 'antd'
import { useTranslation } from 'react-i18next'
import { addValidationOnError } from '../../util'
import { AnswerInput } from '../api'
import { useSaveAnswer } from '../hooks'

export type AnswerFormProps = {
    siteId: string
    /** The visitor's question; "Change" lets it be tidied up, so Evaa recognises questions like it. */
    question: string
    /** Under the question, for example how often it was asked. */
    meta?: React.ReactNode
    submitLabel: string
    cancelLabel: string
    onSaved: () => void
    onCancel: () => void
}

/**
 * Adds an answer to a question Evaa could not answer, in place: in the queue of unanswered questions and under the
 * message in a chat.
 */
export function AnswerForm({ siteId, question, meta, submitLabel, cancelLabel, onSaved, onCancel }: AnswerFormProps) {
    const { t } = useTranslation()
    const [form] = Form.useForm<AnswerInput>()
    const [editing, setEditing] = useState(false)
    const save = useSaveAnswer(siteId)
    const current = Form.useWatch('question', form) ?? question

    return (
        <Form
            form={form}
            layout="vertical"
            requiredMark={false}
            initialValues={{ question, answer: '' }}
            onFinish={(input) =>
                save
                    .mutateAsync({ input: { ...input, askedQuestion: question } })
                    .then(onSaved)
                    .catch(addValidationOnError(form))
            }
        >
            {editing ? (
                <Form.Item
                    name="question"
                    label={t('Question')}
                    extra={t('Write it the way visitors ask it, so Evaa recognises questions like it.')}
                    rules={[{ required: true, message: t('Enter the question') }]}
                    className="mb-3"
                >
                    <Input
                        autoFocus
                        maxLength={500}
                    />
                </Form.Item>
            ) : (
                <Flex
                    vertical
                    gap={2}
                    className="mb-3"
                >
                    <Flex
                        gap="small"
                        align="baseline"
                        wrap
                    >
                        <Typography.Text className="text-base font-medium">“{current}”</Typography.Text>
                        <Button
                            type="link"
                            size="small"
                            className="px-0"
                            onClick={() => setEditing(true)}
                        >
                            {t('Change')}
                        </Button>
                    </Flex>
                    {meta}
                    <Form.Item
                        name="question"
                        hidden
                    >
                        <Input />
                    </Form.Item>
                </Flex>
            )}
            <Form.Item
                name="answer"
                label={t('Your answer')}
                extra={t(
                    'Evaa uses this answer from now on, for this question and questions like it. It does not change your website.'
                )}
                rules={[{ required: true, message: t('Enter the answer') }]}
                className="mb-3"
            >
                <Input.TextArea
                    autoFocus={!editing}
                    autoSize={{ minRows: 2, maxRows: 6 }}
                    maxLength={5000}
                    placeholder={t('Yes, we pay half of the childcare costs. Ask your recruiter about the details.')}
                />
            </Form.Item>
            <Flex gap="small">
                <Button
                    type="primary"
                    htmlType="submit"
                    loading={save.isPending}
                >
                    {submitLabel}
                </Button>
                <Button
                    type="text"
                    onClick={onCancel}
                >
                    {cancelLabel}
                </Button>
            </Flex>
        </Form>
    )
}
